import { useMemo } from "react";
import { AlertTriangle, Calendar, Users, ChevronRight, Pencil, Eye } from "lucide-react";
import { useI18n } from '@/hooks/useI18n';
import { Button } from "@/components/ui/button";
import { VerticalScrollableContentWithArrows } from "@/components/ui/vertical-scrollable-content-with-arrows";
import { ItineraryData } from "./models/itinerary.model";
import { ItineraryBuilder } from "./models/itinerary-builder.model";
import { getStorageThumbnailUrl } from "@/utils/imageStorage";
import { DayView } from "./models/itinerary-day.model";
import { formatDayDate, formatDayTitle, formatDayWeekday } from "./components/day-labels";
import { cn } from "@/components/utils/utils";
import { UnsplashAttribution } from "@/components/ui/UnsplashAttribution";

interface ItinerarySidebarProps {
  data: ItineraryData;
  builder?: ItineraryBuilder | null;
  onEditTrip?: () => void;
  onDaySelect?: (date: string) => void;
  selectedDate?: string;
  isReadOnly?: boolean;
}

const ItinerarySidebar = ({ data, builder, onEditTrip, onDaySelect, selectedDate, isReadOnly = false }: ItinerarySidebarProps) => {
  const { t, language } = useI18n();
  const locale = language || 'it-IT';

  // Il builder ha lo stato più fresco; senza builder si mostra ciò che è persistito.
  const days: DayView[] = builder ? builder.getAllDays() : (data?.itinerary_days || []);

  // Un template non ha date: al loro posto mostra la durata.
  const isTemplate = !!data?.isTemplate;

  const headerSubtitle = isTemplate
    ? t('builder.durationDays', { count: data?.duration_days || days.length })
    : data?.start_date && data?.end_date
      ? `${formatDayDate(data.start_date, locale)} - ${formatDayDate(data.end_date, locale)}`
      : '';

  const menuItems = useMemo(
    () => days.map(day => ({
      id: day.key,
      label: formatDayTitle(day, { locale, fallback: number => t('builder.dayNumber', { number }) }),
      description: [formatDayWeekday(day, locale), t('itinerarySidebar.dayActivitiesCount', { count: day.activities.length })]
        .filter(Boolean)
        .join(' • '),
      date: day.key,
      // Fuori dalla finestra della travel request: il giorno resta, ma segnalato.
      exceeds: !!day.exceedsTravelRequest,
    })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [days, locale, t],
  );

  return (
    // show on mobile (full width) and use side column on desktop
    // Su mobile e' un blocco in flusso naturale (altezza dal contenuto): con h-full
    // dentro un contenitore a colonna si prendeva tutta l'altezza disponibile
    // schiacciando il pannello dei giorni. Colonna sticky solo da lg in su.
    <aside className="block w-full lg:w-80 lg:h-full bg-white border-r border-gray-200 flex flex-col lg:sticky lg:top-0">
      {/* Header: compact on mobile (image - title - desc in one line) and full on desktop */}
      {/* Mobile compact header */}
      <div className="flex items-center gap-3 p-3 border-b lg:hidden">
        {data?.image ? (
          <div
            className="w-10 h-10 bg-cover bg-center rounded-md flex-shrink-0"
            style={{ backgroundImage: `url(${getStorageThumbnailUrl(data.image.url, { noTransform: true })})` }}
            aria-hidden
          />
        ) : (
          <div className="w-10 h-10 bg-gray-100 rounded-md flex-shrink-0" />
        )}
        <div className="min-w-0 flex-1">
          <h4 className="text-gray-900 truncate">{data?.title || t('itinerarySidebar.untitledTrip')}</h4>
          <p className="mt-1 text-sm text-gray-500 truncate">{headerSubtitle} — {data?.total_guests} {data?.total_guests === 1 ? t('common.guest') : t('common.guests')}</p>
        </div>
        {!isReadOnly && onEditTrip && (
          <Button variant="ghost" size="icon" className="flex-shrink-0 h-8 w-8" onClick={onEditTrip}>
            <Pencil className="h-4 w-4 text-gray-500" />
          </Button>
        )}
        {isReadOnly && onEditTrip && (
          <Button variant="ghost" size="icon" className="flex-shrink-0 h-8 w-8" onClick={onEditTrip}>
            <Eye className="h-4 w-4 text-gray-500" />
          </Button>
        )}
      </div>

      {/* Mobile: horizontal days bar (scrollable) - keep it visible under compact header */}
      <div className="mt-2 mb-2 px-3 block lg:hidden">
        <div className="overflow-x-auto -mx-3 px-3 scrollbar-hide">
          <div className="flex gap-3 mb-2">
            {menuItems.map(item => (
              <button
                key={item.id}
                onClick={() => onDaySelect?.(item.date)}
                className={cn(
                  'flex-shrink-0 min-w-[50px] min-h-[40px] px-3 py-2 rounded-lg border transition-colors text-left',
                  selectedDate === item.date ? 'bg-gray-100 border-gray-300' : 'bg-white border-gray-200 hover:bg-gray-50',
                  item.exceeds && 'border-red-300 bg-red-50',
                )}
                title={item.exceeds ? t('builder.dayExceedsRequest') : undefined}
              >
                <div className={cn('font-medium', item.exceeds ? 'text-red-700' : 'text-gray-900')}>{item.label}</div>
                <div className="hidden sm:block text-sm text-gray-500">{item.description}</div>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Desktop full header */}
      <div className="hidden lg:block p-6 border-b border-gray-200">
        {data?.image && (
          <div className="mb-4">
            <div className="relative w-full h-20 overflow-hidden rounded-lg">
              <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${getStorageThumbnailUrl(data.image.url, { noTransform: true })})` }} />
              {/* Attribuzione richiesta ovunque la foto Unsplash sia mostrata,
                  flusso viaggiatore incluso (questa sidebar e' usata anche in shared-proposal). */}
              <UnsplashAttribution item={data.image} />
            </div>
          </div>
        )}
        <h4 className="text-gray-900 mb-4 overflow-hidden" style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
          {data?.title || t('itinerarySidebar.untitledTrip')}
        </h4>
        <div className="space-y-3">
          <div className="flex items-center gap-2 text-gray-600">
            <Calendar className="h-4 w-4 text-gray-400" />
            <span>{headerSubtitle}</span>
          </div>
          <div className="flex items-center gap-2 text-gray-600">
            <Users className="h-4 w-4 text-gray-400" />
            <span>{data?.total_guests} {data?.total_guests === 1 ? t('common.guest') : t('common.guests')}</span>
          </div>
        </div>
        {onEditTrip && (
          <Button variant="outline" size="sm" className="mt-4 w-full flex items-center gap-2" onClick={onEditTrip}>
            {isReadOnly ? <Eye className="h-4 w-4" /> : <Pencil className="h-4 w-4" />}
            {isReadOnly ? t('itinerarySidebar.viewDetails') : t('itinerarySidebar.editDetails')}
          </Button>
        )}
      </div>

      {/* Navigation Menu - show only on desktop (vertical) */}
      <VerticalScrollableContentWithArrows
        className="hidden lg:block flex-1"
        viewportClassName="h-full"
      >
        <nav className="space-y-2 p-2">
          {menuItems.length > 0 ? (
            menuItems.map((item) => (
              <button
                key={item.id}
                className={cn(
                  'w-full text-left p-3 rounded-lg transition-colors group',
                  selectedDate === item.date ? 'bg-gray-100' : 'hover:bg-gray-50',
                  item.exceeds && 'bg-red-50/60 hover:bg-red-50',
                )}
                onClick={() => onDaySelect?.(item.date)}
                title={item.exceeds ? t('builder.dayExceedsRequest') : undefined}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className={cn('font-medium flex items-center gap-1.5', item.exceeds ? 'text-red-700' : 'text-gray-900')}>
                      {item.exceeds && <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0" />}
                      {item.label}
                    </div>
                    <div className={cn('text-sm', item.exceeds ? 'text-red-600' : 'text-gray-500')}>
                      {item.exceeds ? t('builder.dayExceedsRequest') : item.description}
                    </div>
                  </div>
                  <ChevronRight className="h-4 w-4 text-gray-400 group-hover:text-gray-600" />
                </div>
              </button>
            ))
          ) : (
            <div className="text-center py-8 text-gray-500">
              <Calendar className="h-8 w-8 mx-auto mb-2 text-gray-300" />
              <p className="">{t('itinerarySidebar.selectDatesToSeeDays')}</p>
            </div>
          )}
        </nav>
      </VerticalScrollableContentWithArrows>
    </aside>
  );
};

export default ItinerarySidebar;
